import { type ToneName, tones } from '@shkola/design';
import { type ReactNode, useId } from 'react';

/*
 * The pictures of «Скло» (27.10, ref. «Duomo»): a white thing standing on a frosted hexagonal pedestal of
 * its card's tone, soft clouds around it, one of them frosted over its foot. The thing is the card's own
 * (an alarm clock for what is late, a sheet with ticks for a test…). The greeting has no picture (his
 * words, 27.10). Decoration only: hidden from screen readers.
 */

const WHITE = '#FFFFFF';

/** «#E5616A» → «rgb(229 97 106 / a)» */
const alpha = (hex: string, a: number) => {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255} / ${a})`;
};

/** Solid colours between two: the faces of the pedestal must not show through one another. */
const mix = (from: string, to: string, k: number) => {
  const a = Number.parseInt(from.slice(1), 16);
  const b = Number.parseInt(to.slice(1), 16);
  const ch = (shift: number) =>
    Math.round(((a >> shift) & 255) * (1 - k) + ((b >> shift) & 255) * k)
      .toString(16)
      .padStart(2, '0');
  return `#${ch(16)}${ch(8)}${ch(0)}`;
};

/** A cloud of three puffs. */
function Cloud({
  x,
  y,
  s,
  opacity = 0.9,
  blur,
}: {
  x: number;
  y: number;
  s: number;
  opacity?: number;
  blur?: string;
}) {
  return (
    <g
      transform={`translate(${x} ${y}) scale(${s})`}
      fill={WHITE}
      fillOpacity={opacity}
      filter={blur ? `url(#${blur})` : undefined}
    >
      <circle cx="0" cy="0" r="12" />
      <circle cx="16" cy="-8" r="16" />
      <circle cx="33" cy="0" r="11" />
      <rect x="0" y="-3" width="33" height="15" rx="7.5" />
    </g>
  );
}

// the pedestal: a hexagon seen from above at an angle (pointy top, squashed), with a depth
const TOP = '120,69 200,95 200,145 120,171 40,145 40,95';
const LEFT = '40,145 120,171 120,205 40,179';
const RIGHT = '120,171 200,145 200,179 120,205';

/** Where a thing stands on its pedestal: drawn in a box of 120, its foot on the middle of the top. */
export const ON_PEDESTAL = { x: 56, y: 4, width: 128, height: 128 } as const;

/** A white thing on its pedestal (`thing` — a picture placed with `ON_PEDESTAL`). */
export function Pedestal({
  tone,
  thing,
  className,
}: {
  tone: ToneName;
  thing: ReactNode;
  className?: string;
}) {
  const id = useId().replace(/:/g, '');
  const t = tones[tone];
  return (
    <svg
      viewBox="0 -24 240 238"
      aria-hidden
      focusable="false"
      className={`pointer-events-none overflow-visible select-none ${className ?? ''}`}
    >
      <defs>
        <filter id={`${id}-soft`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id={`${id}-haze`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
        <linearGradient id={`${id}-top`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={WHITE} stopOpacity="0.55" />
          <stop offset="1" stopColor={WHITE} stopOpacity="0.1" />
        </linearGradient>
        <radialGradient id={`${id}-glow`} cx="0.5" cy="0.45" r="0.5">
          <stop offset="0" stopColor={WHITE} stopOpacity="0.85" />
          <stop offset="1" stopColor={WHITE} stopOpacity="0" />
        </radialGradient>
      </defs>
      <Cloud x={6} y={60} s={1.05} opacity={0.75} blur={`${id}-haze`} />
      <Cloud x={176} y={40} s={0.8} opacity={0.7} blur={`${id}-haze`} />
      <g strokeLinejoin="round" strokeWidth="12">
        <polygon points={LEFT} fill={mix(t.to, t.icon, 0.2)} stroke={mix(t.to, t.icon, 0.2)} />
        <polygon points={RIGHT} fill={mix(t.to, t.icon, 0.3)} stroke={mix(t.to, t.icon, 0.3)} />
        <polygon points={TOP} fill={mix(t.to, t.icon, 0.08)} stroke={mix(t.to, t.icon, 0.08)} />
        <polygon points={TOP} fill={`url(#${id}-top)`} stroke="none" />
      </g>
      <ellipse cx="120" cy="112" rx="70" ry="38" fill={`url(#${id}-glow)`} />
      <ellipse
        cx="120"
        cy="128"
        rx="42"
        ry="11"
        fill={alpha(t.icon, 0.3)}
        filter={`url(#${id}-soft)`}
      />
      {thing}
      <Cloud x={150} y={150} s={1.1} opacity={0.62} blur={`${id}-haze`} />
    </svg>
  );
}
